import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, signal, TemplateRef, viewChild } from '@angular/core';
import { FilterChipComponent } from 'src/app/home-page/components/filter-chip/filter-chip.component';
import { SectionTitleComponent } from 'src/app/home-page/components/section-title/section-title.component';
import { Subject, takeUntil } from 'rxjs';
import { CarouselArrowsComponent } from 'src/app/home-page/components/carousel-arrows/carousel-arrows.component';
import { ProductCardComponent } from '@core/components/product-card/product-card.component';
import { CarouselBaseComponent } from 'src/app/home-page/components/carousel-base/carousel-base.component';
import { CarouselService } from '../../services/carousel.service';
import { Product } from '@core/models/piramide/Product';
import { CarouselItemBase } from 'src/app/home-page/components/carousel-base/carousel-item-base.model';
import { FilterChipBaseModel } from 'src/app/home-page/components/filter-chip/filter-chip-base.model';
import { FilterChipTypeEnum } from 'src/app/home-page/components/filter-chip/filter-chip-type.enum';
import { SwiperOptions } from 'swiper/types';
import { CAROUSEL_BASE_DEFAULT_CONFIG } from 'src/app/home-page/components/carousel-base/carousel-base.default-config';
import { Autoplay, Grid, Pagination } from 'swiper/modules';

@Component({
  selector: 'cgw-highlighted-products-section',
  standalone: true,
  imports: [
    SectionTitleComponent,
    CommonModule,
    CarouselArrowsComponent,
    CarouselBaseComponent,
    FilterChipComponent,
    ProductCardComponent,
  ],
  templateUrl: './highlighted-products-section.component.html',
  styleUrl: './highlighted-products-section.component.scss',
})
export class HighlightedProductsSectionComponent implements OnInit, OnDestroy {
  private readonly _productTemplateRef = viewChild('productTemplate', { read: TemplateRef });
  private readonly _destroyS = new Subject<boolean>();

  public readonly carousel =
    viewChild<CarouselBaseComponent<Product, CarouselItemBase<Product>>>(CarouselBaseComponent);
  public readonly carouselConfig = signal<SwiperOptions>({
    ...CAROUSEL_BASE_DEFAULT_CONFIG,
    modules: [Autoplay, Pagination, Grid],
    slidesPerView: 1,
    spaceBetween: 10,
    grid: {
      rows: 2,
      fill: 'row',
    },
    breakpoints: {
      768: {
        slidesPerView: 2,
        grid: {
          rows: 2,
          fill: 'row',
        },
      },
      992: {
        slidesPerView: 3,
        grid: {
          rows: 2,
          fill: 'row',
        },
      },
      1400: {
        slidesPerView: 3,
        grid: {
          rows: 2,
          fill: 'row',
        },
      },
    },
  });
  public readonly carouselItems = signal<CarouselItemBase<Product>[]>([]);
  public readonly filterChips = signal<FilterChipBaseModel<Product[]>[]>([]);
  public readonly productList = signal<Product[]>([]);

  constructor(private carouselService: CarouselService) {}

  public ngOnInit(): void {
    this.carouselService
      .getHighlights()
      .pipe(takeUntil(this._destroyS))
      .subscribe((highlighted) => {
        const highlightedProducts: Product[] = highlighted
          .map((highlighted) => highlighted.productos)
          .flat();
        this.filterChips.set(
          highlighted.map(
            (highlight, index) =>
              new FilterChipBaseModel<Product[]>({
                id: highlight.idAgrupador,
                type: FilterChipTypeEnum.grouper,
                icon: null,
                title: highlight.agrupador.nombre,
                disabled: false,
                active: index == 0,
                filterFn: (products) => {
                  this.carousel().slideTo(0);
                  return products.filter(
                    (p) => p?.$subcategory?.id_agrupador == highlight.idAgrupador,
                  );
                },
              }),
          ),
        );
        this.productList.set(highlightedProducts);
        this.setFilteredProducts(
          this.filterChips()
            .find((c) => !!c.active)
            .filterFn(this.productList()),
        );
      });
  }

  public setFilteredProducts(filteredList: Array<Product>): void {
    this.carouselItems.set(
      filteredList.map(
        (p) =>
          new CarouselItemBase({
            id: p.id_producto,
            data: p,
            templateRef: this._productTemplateRef(),
            templateContext: { product: p },
          }),
      ),
    );
  }

  public ngOnDestroy(): void {
    this._destroyS.next(true);
    this._destroyS.complete();
  }
}
